/**
 * One examiner line, arriving as raw speech while it is synthesised
 * (backend /api/interviews/speech/stream: 16-bit little-endian PCM, mono, at
 * the rate the response names). The line is read once and kept, so a line
 * fetched early (prefetch, on the start screen) plays back in full when it is
 * finally spoken, and a listener that subscribes late is caught up first.
 */
export class SpeechStream {
  constructor(open) {
    this.chunks = [];
    this.sampleRate = 24000;
    this.done = false;
    this.error = null;
    this.listeners = new Set();
    this.carry = null; // a lone byte: half a sample, waiting for the next piece
    this.finished = this.read(open);
  }

  async read(open) {
    try {
      const { reader, sampleRate } = await open();
      if (sampleRate) this.sampleRate = sampleRate;
      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        this.push(value);
      }
    } catch (err) {
      this.error = err || new Error('The speech stream failed');
    }
    this.done = true;
    this.listeners.forEach((l) => l.end(this.error));
    this.listeners.clear();
  }

  push(value) {
    let bytes = value;
    if (this.carry) {
      bytes = new Uint8Array(this.carry.length + value.length);
      bytes.set(this.carry, 0);
      bytes.set(value, this.carry.length);
      this.carry = null;
    }
    if (bytes.length % 2) {
      this.carry = bytes.slice(bytes.length - 1);
      bytes = bytes.subarray(0, bytes.length - 1);
    }
    if (!bytes.length) return;
    // A copy on its own buffer, so it reads as whole 16-bit samples.
    const chunk = new Int16Array(bytes.slice().buffer);
    this.chunks.push(chunk);
    this.listeners.forEach((l) => l.chunk(chunk));
  }

  /** chunk(Int16Array) for every piece, past and future; end(error|null) once.
   * Returns an unsubscribe function. */
  subscribe({ chunk, end }) {
    this.chunks.forEach(chunk);
    if (this.done) {
      end(this.error);
      return () => {};
    }
    const listener = { chunk, end };
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get seconds() {
    return this.chunks.reduce((n, c) => n + c.length, 0) / this.sampleRate;
  }
}

/** 16-bit samples as floats in [-1, 1], for Web Audio. */
export function int16ToFloat(samples) {
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) out[i] = samples[i] / 0x8000;
  return out;
}
